import { useMemo } from 'react';
import { getUtcDate, useQuery } from '@common-fe/common-fe';
import dayjs from 'dayjs';

import { api } from '@/api';
import { DEFAULT_DATE_FORMAT } from '@/common/constants';
import { API_URL, IPlanGracePeriodDto } from '@/dtos';

const QUERY_KEY = 'useGetEmployerTerminationPlansListQueryKey';
const HSA_ACCOUNT_TYPE = 'HSA';

const planListFormatter = (data: IPlanGracePeriodDto[]) =>
  data.map((item) => ({
    id: `${item.id}`,
    name: item.name || '',
    startDate: item.start_date || '',
    endDate: item.end_date || null,
    // TODO: the field is not exists
    // @ts-ignore
    gracePeriodDaysAmount: item.grace_period_days_amount,
    claimLastDate: item?.claim_last_date
      ? getUtcDate(item.claim_last_date).format(DEFAULT_DATE_FORMAT)
      : null,
    coverageEndDate: item?.coverage_end_date
      ? getUtcDate(item.coverage_end_date).format(DEFAULT_DATE_FORMAT)
      : null,
  }));

export interface EmployerTerminationPlan {
  id: string;
  name: string;
  startDate: string;
  endDate: string | null;
  gracePeriodDaysAmount?: number;
  claimLastDate: string | null;
  coverageEndDate: string | null;
  endOfCoverageDisabled?: boolean;
}

export const useGetEmployerTerminationPlansListQuery = (
  orgId?: string,
  terminationDate?: string,
  statuses?: string[]
) => {
  const { isLoading, isSuccess, data, refetch } = useQuery(
    [orgId, terminationDate, QUERY_KEY],
    orgId && terminationDate
      ? () =>
          api.get(API_URL.PLANS_ORGANIZATIONS_ACTIVE_GRACE_PERIOD_BY_ORGANIZATION_ID(orgId), {
            params: {
              termination_date: terminationDate,
              ...(statuses && statuses.length > 0 && { plan_statuses: statuses.join(',') }),
            },
          })
      : () => null
  );

  const formattedData = useMemo(() => {
    if (!data?.data) return [];

    return planListFormatter(data.data);
  }, [data]);

  const isHsaAccountExist = useMemo(
    () => data?.data.some((item) => item.account_type === HSA_ACCOUNT_TYPE),
    [data]
  );
  const hsaStartDate = useMemo(() => {
    if (!data?.data) return undefined;

    const hsaPlans = data.data
      .filter((item) => item.account_type === HSA_ACCOUNT_TYPE)
      .sort((a, b) => dayjs(a.start_date).diff(dayjs(b.start_date)));

    const hsaPlan = hsaPlans[0];

    if (
      hsaPlan?.start_date &&
      (dayjs(hsaPlan.start_date).isBefore(dayjs()) || dayjs(hsaPlan.start_date).isSame(dayjs()))
    ) {
      return hsaPlan?.start_date;
    }
    return undefined;
  }, [data]);

  const hsaPlanIds = useMemo(() => {
    if (!data?.data) return [];
    return data.data
      .filter((item) => item.account_type === HSA_ACCOUNT_TYPE)
      .map((item) => item?.id?.toString());
  }, [data]);

  return {
    data: data?.data,
    isHsaAccountExist,
    hsaStartDate,
    formattedData,
    isLoading,
    isSuccess,
    refetch,
    hsaPlanIds,
  };
};
